import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { http } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ReceiptSketch } from "../components/Decor";
import { Wordmark } from "../components/Brand";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX = 20;

export default function PaymentSuccess() {
  const [search] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = search.get("session_id") || "";
  const [status, setStatus] = useState("polling");
  const [message, setMessage] = useState("Vérification du paiement…");
  const timerRef = useRef(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); setMessage("Session introuvable."); return; }
    const check = async () => {
      try {
        const r = await http.get(`/billing/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid"); setMessage("Abonnement activé — bienvenue !");
          try { await refresh?.(); } catch {}
          setTimeout(() => nav("/aujourdhui", { replace: true }), 1500);
          return;
        }
        if (r.data.payment_status === "expired" || r.data.status === "expired") {
          setStatus("expired"); setMessage("La session a expiré. Vous pouvez réessayer depuis Paramètres.");
          return;
        }
      } catch {}
      tries.current += 1;
      if (tries.current >= POLL_MAX) {
        setStatus("error");
        setMessage("Nous n'avons pas encore reçu la confirmation. Rafraîchissez la page dans quelques instants.");
        return;
      }
      timerRef.current = setTimeout(check, POLL_INTERVAL_MS);
    };
    check();
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [sessionId, nav, refresh]);

  return (
    <div className="min-h-screen bg-[#0B1F33] text-white relative overflow-hidden" data-testid="payment-success-page">
      <div aria-hidden className="absolute inset-0 pat-grid-ink opacity-40" />
      <div aria-hidden className="absolute -top-10 -right-10 opacity-30"><ReceiptSketch w={220} tone="dark" /></div>
      <div className="relative max-w-6xl mx-auto w-full px-5 py-6">
        <Link to="/aujourdhui" className="flex items-center gap-2 w-fit">
          <Wordmark tone="dark" size="md" />
        </Link>
      </div>
      <div className="relative min-h-[72vh] flex items-center justify-center px-5 pb-12">
        <div className="w-full max-w-md rounded-xl border border-[#CDD7E3] bg-white p-8 text-center shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: status === "paid" ? "#E4F4EA" : "#FFF0D2" }}>
            {status === "paid"
              ? <CheckCircle2 className="w-8 h-8 text-[#24845B]" />
              : <Loader2 className="w-8 h-8 animate-spin text-[#F58F06]" />}
          </div>
          <h1 className="mt-5 text-[22px] font-heading font-bold text-[#111111]" data-testid="payment-status-title">
            {status === "paid" ? "Paiement confirmé" : status === "expired" ? "Session expirée" : status === "error" ? "En attente" : "Paiement en cours"}
          </h1>
          <p className="mt-2 text-[13.5px] text-[#5E6875]" data-testid="payment-status-message">{message}</p>
          {(status === "expired" || status === "error") && (
            <button onClick={() => nav("/parametres")} className="mt-5 h-11 px-6 rounded-md text-[#111111] text-[13.5px] font-semibold bg-[#FF9F1C] hover:bg-[#F58F06]">
              Retour aux paramètres
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
